import React, { useState, useRef, useEffect } from 'react';
import DashedLine from '@components/DashedLine';
import useResizeObserver from '@hooks/useResizeObserver';
import * as styles from './FAQ.module.scss';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import tracker from '@utils/tracker';

export default React.memo(function FAQ({ items }) {
  return !items?.length ? null : (
    <section
      id="calculator-faqs"
      className="section bg-gray-100"
    >
      <div className={styles.wrapper}>
        <div className="content-wrapper">
          <h2>Frequently Asked Questions</h2>
        </div>

        <ul className={styles.list}>
          {items.map((item, index) => (
            <Item
              key={index}
              {...item}
            />
          ))}
        </ul>
      </div>
    </section>
  );
});

function Item({ name, question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  const questionId = useRef(uniqueId('question-'));
  const answerId = useRef(uniqueId('answer-'));
  const [hideBg, setHideBg] = useState(false);

  question = question?.trim();
  answer = answer?.trim();

  if (!question) {
    question = name?.trim();
  }

  useEffect(() => {
    if (isOpen) {
      tracker.track(tracker.actions.OPEN_FAQ, { question });
    }
  }, [isOpen, question]);

  return !question || !answer ? null : (
    <li className={classNames({ [styles.open]: isOpen })}>
      <button
        id={questionId.current}
        className={classNames(styles.question, { [styles.hideBg]: hideBg })}
        onClick={() => {
          setIsOpen(!isOpen);
          setHideBg(true);
        }}
        onMouseLeave={() => setHideBg(false)}
        aria-expanded={isOpen}
        aria-controls={answerId.current}
      >
        <div className={classNames('content-wrapper', styles.text)}>
          {question}
        </div>
      </button>

      <div className="content-wrapper">
        <Answer
          id={answerId.current}
          isOpen={isOpen}
          html={answer}
          aria-labelledby={questionId.current}
        />
        <DashedLine
          direction="horizontal"
          color="#dddfe3"
          className={styles.divider}
        />
      </div>
    </li>
  );
}

function Answer({ isOpen, html, style = {}, ...rest }) {
  const ref = useRef();
  const { height = 0 } = useResizeObserver(ref);

  return (
    <div
      className={styles.answerWrapper}
      style={{ maxHeight: isOpen ? height : 0 }}
      aria-hidden={!isOpen}
      {...rest}
    >
      <div
        ref={ref}
        className={styles.answer}
        role="region"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
